<template>
  <layout-basic>
    <page-hero
      eyebrow="Experience IMPROVE"
      title="Take an interactive tour of IMPROVE"
      lead="Start a 14-day trial and we’ll walk you through the product in your own account — plus IMPROVE Live if you’d rather talk it through with a person. No credit card required."
    />

    <section class="exp-steps">
      <v-container>
        <div class="exp-steps__grid">
          <article v-for="(step, i) in steps" :key="step.title" class="exp-step">
            <span class="exp-step__num">{{ i + 1 }}</span>
            <h3 class="font-sora">{{ step.title }}</h3>
            <p>{{ step.body }}</p>
          </article>
        </div>
      </v-container>
    </section>

    <section class="exp-next">
      <v-container class="exp-next__inner">
        <h2 class="font-sora">The tour lives in your trial</h2>
        <p class="exp-next__lead">
          There is no separate video or click-through on this page. Create your
          free trial and you’ll get the interactive tour straight away. If that
          isn’t enough, IMPROVE Live is offered in the same flow.
        </p>
        <div class="exp-fork">
          <article class="exp-fork__card">
            <h3 class="font-sora">Start your 14-day trial</h3>
            <p>
              Create an account, take the interactive tour, and explore IMPROVE
              with your own team.
            </p>
            <a :href="trialHref" class="exp-btn exp-btn--primary" rel="noopener">
              Start 14-day trial
            </a>
          </article>
          <article class="exp-fork__card exp-fork__card--alt">
            <h3 class="font-sora">Prefer a live session?</h3>
            <p>
              Book a free IMPROVE Live webinar: a short live demonstration
              with time for your questions. No account needed.
            </p>
            <router-link :to="{ path: '/improve-live', hash: '#book' }" class="exp-btn exp-btn--teal">
              Book a webinar
            </router-link>
          </article>
        </div>
        <router-link to="/watch-demo" class="exp-btn exp-btn--ghost exp-next__tertiary">
          Watch the 4-minute demo
        </router-link>
      </v-container>
    </section>

    <point-grid
      title="What you’ll experience"
      :points="highlights"
      :columns="2"
      tint
    />

    <standard-cta
      title="Convinced IMPROVE is right for you?"
      lead="Buy IMPROVE online when you’re ready — or keep exploring first."
    />
  </layout-basic>
</template>

<script>
import LayoutBasic from '@/layouts/Dashboard';
import PageHero from '@/components/marketing/PageHero.vue';
import PointGrid from '@/components/marketing/PointGrid.vue';
import StandardCta from '@/components/marketing/StandardCta.vue';
import { marketingPageMeta } from '@/utils/marketingPage';
import { getTrialSignupHref } from '@/config/ctas';

export default {
  name: 'Experience',
  components: {
    LayoutBasic,
    PageHero,
    PointGrid,
    StandardCta,
  },
  metaInfo() {
    return marketingPageMeta({
      title: 'Experience IMPROVE | Interactive Tour & Free Trial',
      description:
        'Start a 14-day free trial to take an interactive tour of IMPROVE in your own account, or book a free IMPROVE Live webinar.',
      path: '/experience',
    });
  },
  data() {
    return {
      steps: [
        {
          title: 'Start your 14-day trial',
          body: 'Create your account — no credit card required.',
        },
        {
          title: 'Take the interactive tour',
          body: 'We’ll walk you through people, courses, assignments and tracking in the live product.',
        },
        {
          title: 'Book IMPROVE Live if you need it',
          body: 'Want to see it with the team? Book a free live webinar from your account or this website.',
        },
      ],
      highlights: [
        'How new starters are onboarded consistently',
        'How compliance training is assigned and tracked',
        'How you upload your own policies and courses',
        'How managers save time with one simple platform',
        'How learning works on desktop and mobile',
        'How simple SME pricing works in practice',
      ],
    };
  },
  computed: {
    trialHref() {
      return getTrialSignupHref();
    },
  },
};
</script>

<style scoped>
.exp-steps {
  padding: 8px 0 32px;
}

.exp-steps__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 980px;
  margin: 0 auto;
}

.exp-step {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 22px 18px;
}

.exp-step__num {
  display: inline-flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eef8f8;
  color: var(--brand-teal, #08a0a8);
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 12px;
}

.exp-step h3 {
  color: var(--brand-navy, #002458);
  font-size: 18px;
  margin: 0 0 8px;
}

.exp-step p {
  color: var(--brand-grey, #6b7280);
  font-size: 14.5px;
  line-height: 1.45;
  margin: 0;
}

.exp-next {
  padding: 40px 0 16px;
  text-align: center;
}

.exp-next__inner {
  max-width: 880px;
}

.exp-next h2 {
  color: var(--brand-navy, #002458);
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 12px;
}

.exp-next__lead {
  color: var(--brand-grey, #6b7280);
  font-size: 16px;
  line-height: 1.5;
  margin: 0 auto 28px;
  max-width: 640px;
}

.exp-fork {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  text-align: left;
  margin-bottom: 20px;
}

.exp-fork__card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 24px 22px;
  display: flex;
  flex-direction: column;
}

.exp-fork__card--alt {
  border-color: #bfe4e6;
  background: #f6fbfb;
}

.exp-fork__card h3 {
  color: var(--brand-navy, #002458);
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 8px;
}

.exp-fork__card p {
  color: var(--brand-grey, #6b7280);
  font-size: 14.5px;
  line-height: 1.5;
  margin: 0 0 18px;
  flex: 1;
}

.exp-fork__card .exp-btn {
  align-self: flex-start;
}

.exp-next__tertiary {
  margin: 0 auto;
}

.exp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 20px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 15px;
  text-decoration: none;
}

.exp-btn--primary {
  background: var(--brand-navy, #002458);
  color: #fff;
}

.exp-btn--primary:hover {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.exp-btn--teal {
  background: var(--brand-teal, #08a0a8);
  color: #fff;
}

.exp-btn--teal:hover {
  background: var(--brand-teal-dark, #078f96);
  color: #fff;
}

.exp-btn--ghost {
  background: #fff;
  color: var(--brand-navy, #002458);
  border: 1.5px solid #d5dde8;
}

.exp-btn--ghost:hover {
  border-color: var(--brand-teal, #08a0a8);
  color: var(--brand-teal, #08a0a8);
}

@media screen and (max-width: 900px) {
  .exp-steps__grid,
  .exp-fork {
    grid-template-columns: 1fr;
  }

  .exp-fork__card .exp-btn {
    align-self: stretch;
  }
}
</style>
